import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODateOnly } from "@entrilia/shared/utilities/dateUtils";
import adminApi from "../../../../api/adminApi";
import type {
  DataCollectionRequestAuditory,
  DataCollectionRequestInfo,
  DataCollectionRequestStatus,
  SearchDataCollectionRequestRequest,
} from "../../../../api/types/dataCollectionTypes";
import {
  allDataCollectionRequestAuditoryValues,
  allDataCollectionRequestStatuses,
} from "../../../../api/types/dataCollectionTypes";
import {
  dataCollectionRequestAuditoryCaptionsMap,
  dataCollectionRequestStatusCaptionsMap,
} from "../../../../utilities/enumCaptions";
import type { FilterState } from "../../../common/filters/filterState";
import type {
  DateFilter,
  GridSortItem,
  MultiSelectFilter,
  SelectOption,
  TableFilter,
  TableFilterDefinition,
} from "../../../common/filters/filterTypes";

const getDataCollectionRequestTemplates = withErrorHandling(adminApi.getDataRequestTemplates);

const getTemplates = async () => {
  const [templates, error] = await getDataCollectionRequestTemplates();
  if (error) {
    logError(error, "[getSelectOptions] getDataCollectionRequestTemplates");
    return [];
  }
  return templates.map((t) => ({ value: t.templateId, label: t.name })).sort(stringComparerBy((c) => c.label));
};

export const selectOptionsResolver = async (filterId: string): Promise<SelectOption[]> => {
  switch (filterId) {
    case "templateId":
      return getTemplates();
    default: {
      logError(`Unknown async select filterId: ${filterId}`, "[getSelectOptions]");
      return [];
    }
  }
};

export const filterDefinitions: TableFilterDefinition<DataCollectionRequestInfo>[] = [
  {
    id: "status",
    name: "Status",
    type: "multi_select",
    operatorOptions: ["one_of"],
    predefinedOptions: allDataCollectionRequestStatuses.map((status) => ({
      value: status,
      label: dataCollectionRequestStatusCaptionsMap[status],
    })),
  },
  {
    id: "auditory",
    name: "Recipient Type",
    type: "multi_select",
    operatorOptions: ["one_of"],
    predefinedOptions: allDataCollectionRequestAuditoryValues.map((auditory) => ({
      value: auditory,
      label: dataCollectionRequestAuditoryCaptionsMap[auditory] ?? auditory,
    })),
  },
  {
    id: "templateId",
    name: "Template",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "reportingDate",
    name: "Reporting Date",
    type: "date",
    operatorOptions: ["range"],
  },
];

export const getDataRequestsFilterPayload = (
  filterState: FilterState<DataCollectionRequestInfo>
): Omit<SearchDataCollectionRequestRequest, "range"> => {
  const getMultiSelectFilterValues = (filterId: string, visibleFilters: TableFilter<DataCollectionRequestInfo>[]) => {
    const filter = visibleFilters.find((f) => f.id === filterId) as
      MultiSelectFilter<DataCollectionRequestInfo> | undefined;
    const selectedValues = filter?.value?.operator === "one_of" ? filter.value.selectedValues : undefined;
    return selectedValues?.length ? selectedValues : undefined;
  };

  const getDateRangeFilterValues = (filterId: string, visibleFilters: TableFilter<DataCollectionRequestInfo>[]) => {
    const filter = visibleFilters.find((f) => f.id === filterId) as DateFilter<DataCollectionRequestInfo> | undefined;
    if (filter?.value?.operator !== "range") {
      return [undefined, undefined];
    }

    const { range } = filter.value;
    const from = range?.[0];
    const to = range?.[1];
    return [from ? convertISODateOnly(from) : undefined, to ? convertISODateOnly(to) : undefined];
  };

  const getSorting = (gridSortItem: GridSortItem | undefined) => {
    if (gridSortItem === undefined) {
      return undefined;
    }

    let fieldId: string | undefined;
    if (gridSortItem.field === "reportingDate") {
      fieldId = "ReportingDate";
    } else if (gridSortItem.field === "publishedAt") {
      fieldId = "PublishedAt";
    }

    if (fieldId === undefined) {
      return undefined;
    }

    return {
      fieldId,
      ascending: gridSortItem.sort === "asc",
    };
  };

  const statuses = getMultiSelectFilterValues("status", filterState.visibleFilters) as
    DataCollectionRequestStatus[] | undefined;

  const auditory = getMultiSelectFilterValues("auditory", filterState.visibleFilters) as
    DataCollectionRequestAuditory[] | undefined;

  const templateIds = getMultiSelectFilterValues("templateId", filterState.visibleFilters);

  const [reportingDateFrom, reportingDateTo] = getDateRangeFilterValues("reportingDate", filterState.visibleFilters);

  const sort = getSorting(filterState.sorting);

  return {
    searchTerm: filterState.search.value || undefined,
    statuses,
    auditory,
    templateIds,
    reportingDateFrom,
    reportingDateTo,
    sort,
  };
};
